import { getItem, setItem } from './storage'

export function getMediaDeviceId(kind: string): string | undefined {
    const item = getItem(kind)
    return item ? item : undefined
}

export function setMediaDeviceId(kind: string, id: string) {
    setItem(kind, id)
}

export function isProblematicAudioDevice(label: string): boolean {
    const lowerLabel = label.toLowerCase()

    // iPhone Continuity Microphone - часто выбирается по умолчанию на Mac, но не работает
    if (lowerLabel.includes('iphone') || lowerLabel.includes('continuity')) {
        return true
    }

    // iPad микрофоны через Continuity
    if (lowerLabel.includes('ipad')) {
        return true
    }

    // Виртуальные аудио устройства (могут не иметь реального входа)
    if (lowerLabel.includes('virtual') || lowerLabel.includes('loopback')) {
        return true
    }

    // Bluetooth устройства без активного соединения часто показываются, но не работают
    // Не блокируем их полностью, но понижаем приоритет (обрабатывается в getPreferredMicrophone)

    return false
}

export function getMicrophonePriority(label: string): number {
    const lowerLabel = label.toLowerCase()

    // Проблемные устройства - самый низкий приоритет
    if (isProblematicAudioDevice(label)) {
        return 1000
    }

    // Встроенный микрофон Mac - высший приоритет
    if (lowerLabel.includes('macbook') ||
        lowerLabel.includes('built-in') ||
        lowerLabel.includes('встроенн') ||
        lowerLabel.includes('internal')) {
        return 1
    }

    // USB микрофоны/гарнитуры - хороший выбор
    if (lowerLabel.includes('usb')) {
        return 2
    }

    // Внешние мониторы с микрофоном
    if (lowerLabel.includes('display') || lowerLabel.includes('monitor')) {
        return 3
    }

    // AirPods и другие Apple устройства (когда подключены, работают хорошо)
    if (lowerLabel.includes('airpods')) {
        return 4
    }

    // Другие Bluetooth устройства - могут быть ненадёжны
    if (lowerLabel.includes('bluetooth') || lowerLabel.includes('wireless')) {
        return 5
    }

    // Всё остальное - средний приоритет
    return 10
}

export async function getPreferredMicrophone(): Promise<string | undefined> {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const microphones = devices.filter(d => d.kind === 'audioinput')

        if (microphones.length === 0) {
            return undefined
        }

        // Если только один микрофон - выбираем его
        if (microphones.length === 1) {
            return microphones[0].deviceId
        }

        // Сортируем по приоритету
        const sorted = microphones
            .map(mic => ({
                deviceId: mic.deviceId,
                label: mic.label,
                priority: getMicrophonePriority(mic.label)
            }))
            .sort((a, b) => a.priority - b.priority)

        // Возвращаем микрофон с лучшим приоритетом
        const best = sorted[0]

        return best.deviceId
    } catch (error) {
        console.error('Failed to enumerate devices for preferred microphone:', error)
        return undefined
    }
}

export function isSpeakerSelectionSupported(): boolean {
    return 'setSinkId' in HTMLMediaElement.prototype
}

export async function setSpeakerDevice(
    mediaElement: HTMLMediaElement,
    deviceId: string
): Promise<boolean> {
    if (!isSpeakerSelectionSupported()) {
        return false
    }
    try {
        await (mediaElement as any).setSinkId(deviceId)
        return true
    } catch (error) {
        console.warn('Failed to set speaker device:', error)
        return false
    }
}
